// Copy from https://github.com/vercel/next.js/blob/canary/examples/with-sentry/pages/_error.js
// Refs: https://stackoverflow.com/a/71323933

import * as Sentry from "@sentry/nextjs";
import type { NextPageContext } from "next";
import type { ErrorProps } from "next/error";
import NextErrorComponent from "next/error";
import Router from "next/router";

import { getLocalstorageExtraInformation } from "shared/lib/sentry";

const SentryError = ({ statusCode }: ErrorProps) => <NextErrorComponent statusCode={statusCode} />;

SentryError.getInitialProps = async (context: NextPageContext) => {
  const { asPath, err, res } = context;

  const redirectTo404 = () => {
    const hasPosties = asPath ? asPath.includes("/posties") : false;
    const location = hasPosties ? "/posties/404" : "/404";
    if (res) {
      res.writeHead(307, { "cache-control": "no-cache", Location: location });
      res.end();
    } else {
      void Router.replace(location, asPath);
    }
    return {};
  };

  // Returning because we don't want to log 404 errors to Sentry.
  if (res?.statusCode === 404) {
    return redirectTo404();
  }

  Sentry.setExtras(getLocalstorageExtraInformation());

  // Running on the server, the response object (`res`) is available.
  //
  // Next.js will pass an err on the server if a page's data fetching methods
  // Threw or returned a Promise that rejected
  //
  // Running on the client (browser), Next.js will provide an err if:
  //
  //  - a page's `getInitialProps` threw or returned a Promise that rejected
  //  - an exception was thrown somewhere in the React lifecycle (render,
  //    ComponentDidMount, etc) that was caught by Next.js's React Error
  //    Boundary. Read more about what types of exceptions are caught by Error
  //    Boundaries: https://reactjs.org/docs/error-boundaries.html

  if (err) {
    // Flushing before returning is necessary if deploying to Vercel, see
    // https://vercel.com/docs/platform/limits#streaming-responses
    await Sentry.flush(2000);

    return redirectTo404();
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // introduce a bug in Next.js, so record it in Sentry
  Sentry.captureException(
    new Error(`_error.js getInitialProps missing data at path: ${asPath ?? ""}`),
  );
  await Sentry.flush(2000);

  return redirectTo404();
};

export default SentryError;
